import { useEffect, FC } from 'react'
import { connect } from 'react-redux'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { compose, Dispatch } from '@reduxjs/toolkit'
import get from 'lodash/get'

import { RootState } from 'typedRedux/configureStoreWithHistory'
import { updateCurrentUser } from 'actions/authActions'
import { Submit } from 'components/form'
import InlineAlert from 'components/display/InlineAlert'
import IconInfoOutline from 'components/icons/InfoOutline'

import AuthTextField from '../AuthTextField'
import AuthLocationField from '../AuthLocationField'
import locationSchema from '../locationSchema'
import withCurrentVisit from '../withCurrentVisit'
import { FieldWrapper } from '../styles'

interface ConfirmEmailFormProps {
  auth: { loading: boolean; currentUser: RootState['auth']['currentUser'] }
  currentUser: RootState['auth']['currentUser']
  currentVisit?: { location: { name: string; lat: number; lng: number } }
  dispatch: Dispatch<any>
}

interface ConfirmEmailProps {
  onSuccess: () => any
}

interface FormValues {
  user: {
    email: string
    location: string | null
    latitude: number | null
    longitude: number | null
  }
}

const validationSchema = Yup.object({
  user: Yup.object({
    email: Yup.string()
      .required('This field is required')
      .email('Not a valid email'),
    ...locationSchema,
  }),
})

const ConfirmEmail: FC<ConfirmEmailProps & ConfirmEmailFormProps> = ({
  onSuccess,
  auth,
  currentUser,
  currentVisit,
  dispatch,
}) => {
  const form = useForm<FormValues>({
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      user: {
        email: currentUser?.email || '',
        location: currentVisit?.location?.name || '',
        latitude: currentVisit?.location?.lat ?? null,
        longitude: currentVisit?.location?.lng ?? null,
      },
    },
  })

  useEffect(() => {
    const { dirtyFields } = form.formState
    const current = form.getValues()
    const keep = (path: string, fresh: any) =>
      get(dirtyFields, path) ? get(current, path) : fresh
    form.reset({
      user: {
        email: keep('user.email', currentUser?.email || ''),
        location: keep('user.location', currentVisit?.location?.name || ''),
        latitude: keep('user.latitude', currentVisit?.location?.lat ?? null),
        longitude: keep('user.longitude', currentVisit?.location?.lng ?? null),
      },
    })
  }, [
    currentUser?.email,
    currentVisit?.location?.name,
    currentVisit?.location?.lat,
    currentVisit?.location?.lng,
    form,
  ])

  const onSubmit = (formValues: FormValues) => {
    // @ts-ignore -- updateCurrentUser not typed
    dispatch(updateCurrentUser(formValues.user)).then(onSuccess)
  }

  return (
    <FormProvider {...form}>
      <form className="qa-confirm-email" onSubmit={form.handleSubmit(onSubmit)}>
        <FieldWrapper margin="medium">
          <InlineAlert variant="information">
            <IconInfoOutline />
            <p>
              {currentUser?.email ? (
                <>
                  Facebook gave us this email address. Please confirm that this
                  is one you want to use for your Riparide account. If it&apos;s
                  not, you can change it now.
                </>
              ) : (
                <>
                  Facebook didn&apos;t give us your email address. We need this
                  to communicate with you about bookings. Please enter your
                  email address now.
                </>
              )}
            </p>
          </InlineAlert>
        </FieldWrapper>

        <FieldWrapper>
          <AuthTextField
            label="Email"
            name="user[email]"
            type="email"
            noMargin
          />
        </FieldWrapper>

        <FieldWrapper margin="medium">
          <AuthLocationField noMargin />
        </FieldWrapper>

        <FieldWrapper>
          <Submit
            disabled={auth.loading}
            fullwidth
            uppercase={false}
            aria-label="Confirm Facebook login"
          >
            Confirm
          </Submit>
        </FieldWrapper>
      </form>
    </FormProvider>
  )
}

const ConnectedConfirmEmail: FC<ConfirmEmailProps> = compose(
  withCurrentVisit,
  connect(({ auth }: { auth: ConfirmEmailFormProps['auth'] }) => ({
    auth,
    currentUser: auth.currentUser,
  })),
)(ConfirmEmail)

export default ConnectedConfirmEmail
